/**
 * The bounded display-formula evaluator (05 §1.2, concept doc-1 #12).
 *
 * ONE authored formula exists today — `floor((value - 10) / 2)`, the ability
 * modifier every d20 stat block prints beside a score — and the whole reason it
 * is data rather than a line of JavaScript is that the next system's modifier is
 * a different arithmetic over the same shape. `MonsterDetail.js:15` hardcoded
 * it; here the monster family declares it on the `ability_scores` group and the
 * renderer evaluates whatever it says.
 *
 * IT IS DELIBERATELY NOT AN EXPRESSION LANGUAGE. The grammar is closed at:
 *
 *     expr    := term (('+' | '-') term)*
 *     term    := unary (('*' | '/') unary)*
 *     unary   := '-'? primary
 *     primary := number | identifier | call | '(' expr ')'
 *     call    := ('floor' | 'ceil' | 'round' | 'abs' | 'min' | 'max') '(' args ')'
 *
 * An identifier resolves against the scope the caller supplies (`value` is the
 * member's own value; a member key reads a sibling) and NOTHING else — there is
 * no property access, no call to anything not in the six-name table, no string,
 * no comparison. Anything outside the grammar is rejected at parse time and the
 * formula renders nothing, which is the same "an unimplemented style renders
 * nothing" rule the section dispatcher follows.
 *
 * The bound is the point. A formula travels in authored content, and content is
 * edited by people who are not writing code; a grammar that cannot express a
 * side effect cannot acquire one by accident, and `eval` or `new Function` would
 * have made the authored tree executable.
 *
 * FOR DISPLAY ONLY. Nothing computed here is stored, filtered on, or sent to the
 * server — 05 §1.2 is explicit, and it is why a NaN is rendered as nothing
 * rather than propagated.
 */

const FUNCTIONS = {
  floor: (...a) => Math.floor(a[0]),
  ceil: (...a) => Math.ceil(a[0]),
  round: (...a) => Math.round(a[0]),
  abs: (...a) => Math.abs(a[0]),
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
};

const TOKEN = /\s*(\d+(?:\.\d+)?|[A-Za-z_][A-Za-z0-9_]*|[()+\-*/,])/y;

/** Tokenize, or null when the source carries a character the grammar has no name for. */
export function tokenize(source) {
  const tokens = [];
  TOKEN.lastIndex = 0;
  let at = 0;
  while (at < source.length) {
    TOKEN.lastIndex = at;
    const match = TOKEN.exec(source);
    if (!match) {
      // Trailing whitespace is not a token but is not an error either.
      if (/^\s*$/.test(source.slice(at))) break;
      return null;
    }
    tokens.push(match[1]);
    at = TOKEN.lastIndex;
  }
  return tokens;
}

/**
 * Parse a formula into an evaluator `(scope) => number`, or null when it is not
 * in the grammar. Parsing is separate from evaluation so a registry-validation
 * pass can reject a bad formula once rather than on every render.
 */
export function parseFormula(source) {
  if (typeof source !== 'string' || !source.trim()) return null;
  const tokens = tokenize(source);
  if (!tokens || !tokens.length) return null;

  let i = 0;
  const peek = () => tokens[i];
  const eat = (token) => {
    if (tokens[i] !== token) throw new SyntaxError(`expected ${token}`);
    i += 1;
  };

  function parseExpr() {
    let node = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = tokens[i]; i += 1;
      const right = parseTerm();
      const left = node;
      node = op === '+' ? (s) => left(s) + right(s) : (s) => left(s) - right(s);
    }
    return node;
  }

  function parseTerm() {
    let node = parseUnary();
    while (peek() === '*' || peek() === '/') {
      const op = tokens[i]; i += 1;
      const right = parseUnary();
      const left = node;
      node = op === '*' ? (s) => left(s) * right(s) : (s) => left(s) / right(s);
    }
    return node;
  }

  function parseUnary() {
    if (peek() === '-') { i += 1; const operand = parseUnary(); return (s) => -operand(s); }
    return parsePrimary();
  }

  function parsePrimary() {
    const token = peek();
    if (token === undefined) throw new SyntaxError('unexpected end');
    if (token === '(') {
      i += 1;
      const node = parseExpr();
      eat(')');
      return node;
    }
    if (/^\d/.test(token)) { i += 1; const n = Number(token); return () => n; }
    if (/^[A-Za-z_]/.test(token)) {
      i += 1;
      if (peek() === '(') {
        const fn = FUNCTIONS[token];
        if (!fn) throw new SyntaxError(`unknown function ${token}`);
        i += 1;
        const args = [];
        if (peek() !== ')') {
          args.push(parseExpr());
          while (peek() === ',') { i += 1; args.push(parseExpr()); }
        }
        eat(')');
        return (s) => fn(...args.map((a) => a(s)));
      }
      // A bare identifier is a scope lookup and can be nothing else.
      return (s) => Number((s || {})[token]);
    }
    throw new SyntaxError(`unexpected ${token}`);
  }

  try {
    const node = parseExpr();
    if (i !== tokens.length) return null; // trailing junk is not a formula
    return node;
  } catch {
    return null;
  }
}

/**
 * Evaluate a formula against a scope. Returns null when the formula is not in
 * the grammar or the result is not finite — a display formula never invents a
 * number, exactly as the numeric extractor never invents a zero (06 §4.4 rule 4).
 */
export function evaluateFormula(source, scope) {
  const node = parseFormula(source);
  if (!node) return null;
  const result = node(scope);
  return Number.isFinite(result) ? result : null;
}

/**
 * `+4` / `-1` — the sign is part of a modifier, so a positive one is written.
 *
 * ASCII HYPHEN, and that is measured rather than assumed. `signed-list` (the
 * race ability-modifier sentence, R10) writes a real U+2212 MINUS SIGN because
 * `RaceDetail.formatAbilityModifiers` did; the monster ability grid wrote
 * `${raw}` and therefore an ASCII hyphen (`MonsterDetail.js:16`). Two different
 * attributes on two different pages, each reproduced as it shipped — G3's "one
 * format per attribute, applied in every context" is a rule about ONE
 * attribute, and unifying the two typographies is a cosmetic call that would
 * change a rendered character on every monster with a penalty.
 */
export function formatSigned(n) {
  if (!Number.isFinite(n)) return '';
  return n >= 0 ? `+${n}` : String(n);
}

export default evaluateFormula;
